import {
  PerformanceEventCustomPayloads,
  PerformanceEventName,
  PerformanceEventPayload,
  SpanPhase,
  StartedSpan,
  TrackPerformanceEvent,
} from './types';
import { v4 as uuidv4 } from 'uuid';

const DEFAULT_SPAN_TTL_MS = 5 * 60_000;
const DEFAULT_SWEEP_INTERVAL_MS = 60_000;

interface ActiveSpan<E extends PerformanceEventName = PerformanceEventName> {
  parentSpanId?: string;
  spanId: string;
  spanName: E;
  startTimestampMs: number;
  traceId: string;
}

interface SpanPayloadOptions<E extends PerformanceEventName> {
  payload?: Partial<PerformanceEventCustomPayloads[E]>;
}

type StartSpanOptions<E extends PerformanceEventName> =
  SpanPayloadOptions<E> & {
    parentSpanId?: string;
    traceId?: string;
  };

export interface PerformanceSpanManager {
  cancelSpan: <E extends PerformanceEventName>(
    span: StartedSpan<E>,
    options?: SpanPayloadOptions<E>,
  ) => void;
  destroy: () => void;
  endSpan: <E extends PerformanceEventName>(
    span: StartedSpan<E>,
    options?: SpanPayloadOptions<E>,
  ) => void;
  findActiveSpan: <E extends PerformanceEventName>(
    spanName: E,
  ) => StartedSpan<E> | undefined;
  startSpan: <E extends PerformanceEventName>(
    spanName: E,
    options?: StartSpanOptions<E>,
  ) => StartedSpan<E>;
}

export interface CreatePerformanceSpanManagerDeps {
  onError?: (error: unknown) => void;
  trackPerformanceEvent?: TrackPerformanceEvent;
}

// These UUID-derived IDs are W3C-shaped and valid for our current tracing use.
// UUID v4 includes fixed version/variant bits, so these values are not fully
// uniform random byte strings. If we later need stricter OTel-style ID
// generation, we should replace these helpers with raw random-byte generation.
const createDefaultTraceId = () => uuidv4().replace(/-/g, '');
const createDefaultSpanId = () => uuidv4().replace(/-/g, '').slice(0, 16);

const isDevEnvironment = () => typeof __DEV__ !== 'undefined' && __DEV__;

interface EmitEventOptions<E extends PerformanceEventName> {
  activeSpan: ActiveSpan<E>;
  customPayload?: Partial<PerformanceEventCustomPayloads[E]>;
  durationMs?: number;
  phase: SpanPhase;
  timestampMs: number;
}

export const createPerformanceSpanManager = (
  deps: CreatePerformanceSpanManagerDeps = {},
): PerformanceSpanManager => {
  const activeSpans = new Map<string, ActiveSpan>();
  const onError =
    deps.onError ??
    (isDevEnvironment() ? (error: unknown) => console.error(error) : () => {});
  const trackPerformanceEvent = deps.trackPerformanceEvent;
  let sweepIntervalId: ReturnType<typeof setInterval> | undefined;

  const removeSpan = (spanId: string) => {
    const activeSpan = activeSpans.get(spanId);
    if (!activeSpan) {
      return undefined;
    }

    activeSpans.delete(spanId);
    return activeSpan;
  };

  const emitEvent = <E extends PerformanceEventName>(
    options: EmitEventOptions<E>,
  ) => {
    if (!trackPerformanceEvent) {
      return;
    }

    const payload = {
      ...options.customPayload,
      durationMs: options.durationMs,
      parentSpanId: options.activeSpan.parentSpanId,
      phase: options.phase,
      spanId: options.activeSpan.spanId,
      spanName: options.activeSpan.spanName,
      timestampMs: options.timestampMs,
      traceId: options.activeSpan.traceId,
    } as PerformanceEventPayload<E>;

    try {
      trackPerformanceEvent(options.activeSpan.spanName, payload);
    } catch (error) {
      onError(error);
    }
  };

  const stopSweepIfIdle = () => {
    if (activeSpans.size > 0 || !sweepIntervalId) {
      return;
    }

    clearInterval(sweepIntervalId);
    sweepIntervalId = undefined;
  };

  const sweepExpiredSpans = () => {
    const timestampMs = performance.now();

    for (const [spanId, activeSpan] of activeSpans.entries()) {
      if (timestampMs - activeSpan.startTimestampMs < DEFAULT_SPAN_TTL_MS) {
        continue;
      }

      activeSpans.delete(spanId);
      emitEvent({
        activeSpan,
        durationMs: Math.round(timestampMs - activeSpan.startTimestampMs),
        phase: 'cancel',
        timestampMs,
      });
    }

    stopSweepIfIdle();
  };

  const ensureSweepRunning = () => {
    if (sweepIntervalId) {
      return;
    }

    sweepIntervalId = setInterval(sweepExpiredSpans, DEFAULT_SWEEP_INTERVAL_MS);
  };

  const finalizeSpan = <E extends PerformanceEventName>(
    span: StartedSpan<E>,
    phase: 'cancel' | 'end',
    customPayload?: Partial<PerformanceEventCustomPayloads[E]>,
  ) => {
    const timestampMs = performance.now();
    const activeSpan = removeSpan(span.spanId) as ActiveSpan<E> | undefined;

    if (!activeSpan) {
      if (isDevEnvironment()) {
        console.warn(
          `[PerformanceSpanManager] No active span found for "${span.spanName}" (spanId: ${span.spanId})`,
        );
      }
      return;
    }

    emitEvent({
      activeSpan,
      customPayload,
      durationMs: Math.round(timestampMs - activeSpan.startTimestampMs),
      phase,
      timestampMs,
    });
    stopSweepIfIdle();
  };

  const startSpan = <E extends PerformanceEventName>(
    spanName: E,
    options: StartSpanOptions<E> = {},
  ): StartedSpan<E> => {
    const parentSpan = options.parentSpanId
      ? activeSpans.get(options.parentSpanId)
      : undefined;
    const traceId =
      options.traceId ?? parentSpan?.traceId ?? createDefaultTraceId();
    const spanId = createDefaultSpanId();
    const startTimestampMs = performance.now();

    const activeSpan: ActiveSpan<E> = {
      parentSpanId: options.parentSpanId,
      spanId,
      spanName,
      startTimestampMs,
      traceId,
    };

    activeSpans.set(spanId, activeSpan);
    ensureSweepRunning();
    emitEvent({
      activeSpan,
      customPayload: options.payload,
      phase: 'start',
      timestampMs: startTimestampMs,
    });

    return {
      parentSpanId: options.parentSpanId,
      spanId,
      spanName,
      traceId,
    };
  };

  const endSpan = <E extends PerformanceEventName>(
    span: StartedSpan<E>,
    options?: SpanPayloadOptions<E>,
  ) => {
    finalizeSpan(span, 'end', options?.payload);
  };

  const cancelSpan = <E extends PerformanceEventName>(
    span: StartedSpan<E>,
    options?: SpanPayloadOptions<E>,
  ) => {
    finalizeSpan(span, 'cancel', options?.payload);
  };

  /*
   * Returns the active span matching the given name, or undefined if there are
   * zero matches or more than one (ambiguous). This lets distant consumers
   * look up a span they didn't start without holding a direct reference.
   * Callers that manage concurrent same-name spans should use the span returned
   * by startSpan instead.
   *
   * This does a full scan of activeSpans. With the sweeper keeping the map
   * small, this isn't a performance concern — revisit with a secondary index if
   * the span count grows significantly.
   */
  const findActiveSpan = <E extends PerformanceEventName>(
    spanName: E,
  ): StartedSpan<E> | undefined => {
    let match: ActiveSpan<E> | undefined;

    for (const activeSpan of activeSpans.values()) {
      if (activeSpan.spanName === spanName) {
        if (match) {
          // More than one match — ambiguous, return undefined
          return undefined;
        }
        match = activeSpan as ActiveSpan<E>;
      }
    }

    if (!match) {
      return undefined;
    }

    return {
      parentSpanId: match.parentSpanId,
      spanId: match.spanId,
      spanName: match.spanName,
      traceId: match.traceId,
    };
  };

  const destroy = () => {
    if (sweepIntervalId) {
      clearInterval(sweepIntervalId);
      sweepIntervalId = undefined;
    }

    activeSpans.clear();
  };

  return {
    cancelSpan,
    destroy,
    endSpan,
    findActiveSpan,
    startSpan,
  };
};
